import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { LabeledInput } from 'Components';
import { AutoCompleteInput, Search, SearchError } from 'Services';
import { useEffectOnce } from 'Hooks';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  failedPolicyMessage?: string;
  containerClassName?: string;
  fetchFunc: (
    setSearchResults: React.Dispatch<React.SetStateAction<Search[]>>,
    setSearchErrorResult?: React.Dispatch<React.SetStateAction<Search[]>>,
  ) => (payload: AutoCompleteInput, resolve?: (result: Search[]) => void, reject?: (error: SearchError) => void) => void;
  onSelectedSearchChange?: (search?: Search) => void;
  onValidationChange?: (valid: boolean | undefined) => void;
  minAutoCompleteLength?: number;
  buildAutocompletePayload?: (value: string) => AutoCompleteInput;
  onEmptySearchResultOrError?: (searchResults: Search[], error?: SearchError) => void;
  runAutoCompleteOnDefaultValue?: boolean;
}

export const AutocompleteInput: React.FC<Props> = ({
  failedPolicyMessage,
  containerClassName,
  className,
  onChange,
  fetchFunc,
  onSelectedSearchChange,
  onValidationChange,
  minAutoCompleteLength,
  buildAutocompletePayload,
  onEmptySearchResultOrError,
  defaultValue,
  runAutoCompleteOnDefaultValue = false,
  ...rest
}) => {
  const [searchResults, setSearchResults] = useState<Search[]>([]);
  const [searchErrorResult, setSearchErrorResult] = useState<unknown>();
  const [highlighted, setHighlighted] = useState(0);
  const [selectedSearch, setSelectedSearch] = useState<Search>();
  const [valid, setValid] = useState<boolean | undefined>(undefined);
  const debounceFunc = fetchFunc(setSearchResults, setSearchErrorResult);
  const [displayResults, setDisplayResults] = useState(true);
  const [autocompleteDefaultValue, setAutocompleteDefaultValue] = useState(defaultValue);

  useEffect(() => {
    if (searchResults.length === 1 && !isForcedSearch(searchResults[0])) {
      setSelectedSearch(searchResults[0]);
    }
  }, [searchResults]);

  useEffect(() => {
    onSelectedSearchChange && onSelectedSearchChange(selectedSearch);
  }, [selectedSearch]);

  const onValidationChangeHandler = (valid: boolean | undefined) => {
    setValid(valid);
    if (!valid) {
      setSelectedSearch(undefined);
    }
    onValidationChange && onValidationChange(valid);
  };

  useEffectOnce(() => {
    if (runAutoCompleteOnDefaultValue && autocompleteDefaultValue) {
      runAutocomplete(autocompleteDefaultValue as string, false, false).then((results: Search[]) => {
        if (results.length !== 1) {
          setSearchResults([]);
          setAutocompleteDefaultValue('');
        }
      });
    }
  });

  const runAutocomplete = async (value: string, selectingSuggestion = false, displayResults = true) => {
    const isValidAutoCompleteLength = (value: string) =>
      (minAutoCompleteLength != null && value.length >= minAutoCompleteLength) || minAutoCompleteLength == null;

    return new Promise<Search[]>((resolve) => {
      if (!selectingSuggestion && isValidAutoCompleteLength(value)) {
        setSearchErrorResult(undefined);
        setSelectedSearch(undefined);
        setHighlighted(0);
        const payload = (buildAutocompletePayload && buildAutocompletePayload(value)) || { input: value };

        debounceFunc(
          payload,
          (results) => {
            if (results.length === 0) {
              onEmptySearchResultOrError && onEmptySearchResultOrError(results, undefined);
              setValid(false);
            } else {
              setDisplayResults(displayResults);
              setValid(true);
            }
            resolve(results);
          },
          (error) => {
            if (error) {
              onEmptySearchResultOrError && onEmptySearchResultOrError([], error);
              setValid(false);
            } else {
              const inputElement = document.getElementsByName(rest.name || '')[0] as HTMLInputElement;
              if (inputElement) {
                inputElement.setCustomValidity('');
              }
              setDisplayResults(displayResults);
              setValid(true);
            }
            resolve([]);
          },
        );
      } else if (minAutoCompleteLength != null && value.length < minAutoCompleteLength) {
        setSearchResults([]);
        resolve([]);
      }
    });
  };

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const eventType = event.nativeEvent?.type;
    const selectingSuggestion = eventType === 'change';
    runAutocomplete(event.target.value, selectingSuggestion);
    onChange && onChange(event);
  };

  const handleClickSearchResult = (search: Search) => {
    setSelectedSearch(search);
    setDisplayResults(false);
  };

  const handleKeys = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      setHighlighted(highlighted < searchResults.length - 1 ? highlighted + 1 : 0);
    } else if (event.key === 'ArrowUp') {
      setHighlighted(highlighted > 0 ? highlighted - 1 : searchResults.length - 1);
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.key === 'Enter' && event.preventDefault();
      searchResults.length > 0 && handleClickSearchResult(searchResults[highlighted]);
    } else if (event.key === 'Escape') {
      setSearchResults([]);
    }
  };

  const isForcedSearch = (search: Search) => search.forced === true;
  const hasOnlyForcedSearch = (searchResults: Search[]) => searchResults.length > 0 && searchResults.every(isForcedSearch);

  return (
    <div className={`relative inline-block w-full ${containerClassName || ''}`}>
      <LabeledInput
        key={`labeled-input-${autocompleteDefaultValue}`}
        failedPolicyMessage={failedPolicyMessage}
        className={className}
        onChange={handleChangeInput}
        onKeyDown={handleKeys}
        onBlur={() => {
          setDisplayResults(false);
        }}
        isValid={valid}
        displayErrorText={!searchErrorResult}
        autoComplete={navigator.userAgent.match(/chrome/i) ? 'chrome-off' : 'off'}
        onValidationChange={onValidationChangeHandler}
        onFocus={() => setDisplayResults(true)}
        extraValidationCheck={() => !searchErrorResult}
        defaultValue={autocompleteDefaultValue}
        {...rest}
      />
      {(searchResults.length > 1 || hasOnlyForcedSearch(searchResults)) && displayResults && valid && (
        <div
          id="search-results"
          className="absolute border-[1px] border-solid border-neutral-300 left-0 right-0 z-[99] max-h-[350px] overflow-y-auto"
        >
          {searchResults.map((search: Search, index) => (
            <div
              key={'address-' + index}
              ref={(el) => {
                if (highlighted === index && el && el.scrollIntoView) {
                  el.scrollIntoView({ block: 'nearest' });
                }
              }}
              className={
                'p-[10px] cursor-pointer border-b-[1px] border-b-solid border-neutral-300 text-[14px] text-dark-gray ' +
                `${highlighted === index ? 'bg-navy-blue text-white' : 'bg-white hover:bg-whitesmoke'} ${
                  !isForcedSearch(search) ? 'font-semibold' : ''
                }`
              }
              onMouseDown={() => handleClickSearchResult(search)}
            >
              {search.displayedOutput}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
